import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Star, Trophy, ArrowLeft, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const ChildRewardsStore = () => {
  const [child, setChild] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [selectedReward, setSelectedReward] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const childUser = localStorage.getItem('childUser');
    const childToken = localStorage.getItem('childToken');
    
    if (!childUser || !childToken) {
      navigate('/child/login');
      return;
    }
    
    // Set API token from childToken for authenticated requests
    api.setToken(childToken);
    
    const childData = JSON.parse(childUser);
    setChild(childData);
    
    // Load photo from localStorage immediately for instant display
    const cachedPhotoUrl = localStorage.getItem(`child_photo_${childData.id}`);
    if (cachedPhotoUrl && cachedPhotoUrl !== 'null') {
      setProfilePhoto(cachedPhotoUrl);
    }
    
    loadRewardsData(childData.id);
  }, [navigate]);

  // Countdown timer for reward expiration
  useEffect(() => {
    const updateCountdown = () => {
      // October rewards expire on Oct 31, 2025 at 11:59:59 PM
      const expirationDate = new Date('2025-10-31T23:59:59');
      const now = new Date();
      const timeLeft = expirationDate - now;
      
      if (timeLeft <= 0) {
        setCountdown('Expired');
        return;
      }
      
      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      
      if (days > 0) {
        setCountdown(`${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''} left`);
      } else if (hours > 0) {
        setCountdown(`${hours} hour${hours !== 1 ? 's' : ''} ${minutes} min left`);
      } else if (minutes > 0) {
        setCountdown(`${minutes} min ${seconds} sec left`);
      } else {
        setCountdown(`${seconds} sec left`);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const loadRewardsData = async (childId) => {
    try {
      setLoading(true);
      console.log('Child rewards: Loading rewards data...');
      
      // Fetch only active rewards from backend (much faster)
      const [rewardsData, purchasesData] = await Promise.all([
        api.getRewardItems({ active: true }),  // Only fetch currently active rewards
        api.getRewardPurchases(childId)
      ]);
      
      console.log('Child rewards: Loaded', rewardsData?.length, 'active rewards');
      
      setRewards(rewardsData || []);
      
      setPurchases(purchasesData);
    } catch (error) {
      console.error('Child rewards: Error loading rewards:', error);
      toast({
        title: 'Error',
        description: 'Failed to load rewards. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (reward) => {
    setSelectedReward(reward);
    setIsConfirming(true);
  };

  const confirmPurchase = async () => {
    if (!child || !selectedReward) {
      console.log('Missing child or selected reward', { child, selectedReward });
      return;
    }

    console.log('Starting purchase:', {
      rewardId: selectedReward.id,
      childId: child.id,
      currentTokens: child.tokens,
      rewardCost: selectedReward.cost
    });

    try {
      const result = await api.purchaseReward(selectedReward.id, child.id);
      console.log('Purchase successful:', result);
      
      // Update child's token balance
      const updatedChild = { ...child, tokens: result.new_token_balance };
      setChild(updatedChild);
      localStorage.setItem('childUser', JSON.stringify(updatedChild));

      // Reload purchases to reflect new purchase
      await loadRewardsData(child.id);

      toast({
        title: 'Reward Unlocked! 🎉',
        description: `You got ${selectedReward.name}! Check "My Collection" to see it.`
      });

      setIsConfirming(false);
      setSelectedReward(null);
    } catch (error) {
      console.error('Purchase error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.status
      });
      toast({
        title: 'Purchase Failed',
        description: error.message || 'Unable to purchase reward. Please try again.',
        variant: 'destructive'
      });
      setIsConfirming(false);
    }
  };

  if (!child || loading) return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="text-xl text-slate-600">Loading amazing rewards...</p>
        <p className="text-sm text-slate-500 mt-2">This may take a moment</p>
      </div>
    </div>
  );

  const earnedRewardIds = purchases.map(p => p.reward_id);

  const canAfford = (cost) => child.tokens >= cost;
  const alreadyOwned = (rewardId) => earnedRewardIds.includes(rewardId);

  const stickers = rewards.filter(r => r.reward_type === 'sticker');
  const pets = rewards.filter(r => r.reward_type === 'pet');

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-orange-600 text-white p-6 shadow-lg">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            {/* Profile Photo */}
            {child && (
              <div
                className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl font-bold shadow-lg overflow-hidden cursor-pointer"
                style={{ color: child.profileColor }}
                onClick={() => navigate('/child/dashboard')}
              >
                {profilePhoto ? (
                  <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  child.name.charAt(0)
                )}
              </div>
            )}
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/child/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Rewards Store 🎁</h1>
              <p className="text-pink-100 text-lg">Spend your tokens on awesome prizes!</p>
              {countdown && countdown !== 'Expired' && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-yellow-300 text-lg">⏰</span>
                  <p className="text-pink-100 text-base font-semibold">
                    October rewards end in: {countdown}
                  </p>
                </div>
              )}
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-8 py-4 rounded-2xl">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-yellow-300" />
                <div>
                  <p className="text-sm text-pink-100">Your Tokens</p>
                  <p className="text-3xl font-bold">{child.tokens}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stickers Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-8 h-8 text-pink-600" />
            <h2 className="text-3xl font-bold text-slate-900">Stickers</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {stickers.map(reward => (
              <Card
                key={reward.id}
                className={`overflow-hidden border-0 shadow-md ${
                  alreadyOwned(reward.id)
                    ? 'bg-slate-100'
                    : 'bg-white hover:shadow-lg transition-shadow'
                }`}
              >
                {/* Image Container - Fixed aspect ratio */}
                <div className="aspect-square bg-gradient-to-br from-pink-50 to-orange-50 p-4 flex items-center justify-center">
                  {reward.image_url ? (
                    <img 
                      src={reward.image_url} 
                      alt={reward.name} 
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-6xl">{reward.emoji}</div>
                  )}
                </div>
                
                {/* Content Container */}
                <div className="p-3">
                  <h3 className="font-bold text-slate-900 text-sm mb-1 line-clamp-1">{reward.name}</h3>
                  <p className="text-xs text-slate-600 mb-3 line-clamp-2">{reward.description}</p>
                  
                  {/* Action Area */}
                  {alreadyOwned(reward.id) ? (
                    <Badge className="w-full bg-green-500 text-white justify-center">Owned! ✓</Badge>
                  ) : (
                    <Button
                      onClick={() => handlePurchase(reward)}
                      disabled={!canAfford(reward.cost)}
                      className={`w-full ${
                        canAfford(reward.cost)
                          ? 'bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600'
                          : 'bg-slate-300 cursor-not-allowed'
                      }`}
                      size="sm"
                    >
                      <Star className="w-4 h-4 mr-1" />
                      {reward.cost} {reward.cost === 1 ? 'Token' : 'Tokens'}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Pets Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="w-8 h-8 text-purple-600" />
            <h2 className="text-3xl font-bold text-slate-900">Pets</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {pets.map(reward => (
              <Card
                key={reward.id}
                className={`overflow-hidden border-0 shadow-md ${
                  alreadyOwned(reward.id)
                    ? 'bg-slate-100'
                    : 'bg-white hover:shadow-lg transition-shadow'
                }`}
              >
                {/* Image Container - Fixed aspect ratio */}
                <div className="aspect-square bg-gradient-to-br from-purple-50 to-pink-50 p-4 flex items-center justify-center">
                  {reward.image_url ? (
                    <img 
                      src={reward.image_url} 
                      alt={reward.name} 
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-6xl">{reward.emoji}</div>
                  )}
                </div>
                
                {/* Content Container */}
                <div className="p-3">
                  <h3 className="font-bold text-slate-900 text-sm mb-1 line-clamp-1">{reward.name}</h3>
                  <p className="text-xs text-slate-600 mb-3 line-clamp-2">{reward.description}</p>
                  
                  {/* Action Area */}
                  {alreadyOwned(reward.id) ? (
                    <Badge className="w-full bg-green-500 text-white justify-center">Owned! ✓</Badge>
                  ) : (
                    <Button
                      onClick={() => handlePurchase(reward)}
                      disabled={!canAfford(reward.cost)}
                      className={`w-full ${
                        canAfford(reward.cost)
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                          : 'bg-slate-300 cursor-not-allowed'
                      }`}
                      size="sm"
                    >
                      <Star className="w-4 h-4 mr-1" />
                      {reward.cost} {reward.cost === 1 ? 'Token' : 'Tokens'}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={isConfirming} onOpenChange={setIsConfirming}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-slate-900">
              Get this reward?
            </DialogTitle>
          </DialogHeader>
          {selectedReward && (
            <div className="text-center space-y-6 py-4">
              <div className="text-8xl">{selectedReward.icon}</div>
              <h3 className="text-2xl font-bold text-slate-900">{selectedReward.name}</h3>
              <p className="text-slate-600">{selectedReward.description}</p>
              <div className="flex items-center justify-center gap-2 text-2xl font-bold">
                <Star className="w-8 h-8 text-amber-500" />
                <span className="text-slate-900">{selectedReward.cost} tokens</span>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={confirmPurchase}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg font-semibold"
                >
                  Yes, Get It!
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsConfirming(false)}
                  className="px-8 py-6"
                >
                  Not Yet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChildRewardsStore;